// frontend/packages/business-console/src/components/content/DocumentField.jsx
//
// Editor for a frontmatter string marked `upryzze:media:document` (a link to
// a PDF / Word / Excel / PowerPoint file, e.g. a board agenda in the site's
// document archive). Upload sends the file to POST /v2/media-assets/document,
// which validates it server-side and stores it in the media library; the
// field gets the file's public URL. Pasting a link still works.
//
// `onUploaded(asset)` lets the parent fill neighbouring fields (the file's
// size) from the upload result.

import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { uploadDocument } from '../../services/mediaService';

const DOCUMENT_ACCEPT =
  '.pdf,.docx,.xlsx,.pptx,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || ''));
}

export default function DocumentField({
  label,
  value,
  required,
  helperText,
  onChangeRaw,
  onUploaded,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadError('');
    setUploading(true);
    try {
      const asset = await uploadDocument(file);
      const url = asset?.publicUrl || asset?.url;
      if (!url) {
        setUploadError(
          'That file uploaded but came back without a link. Please try again.'
        );
        return;
      }
      onChangeRaw?.(url);
      onUploaded?.(asset);
    } catch (err) {
      setUploadError(
        err?.response?.data?.message ||
          'That file did not upload. Use a PDF, Word, Excel, or PowerPoint file and try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
          {label}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={
              uploading ? (
                <CircularProgress size={16} />
              ) : (
                <FileUploadOutlinedIcon />
              )
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{ fontWeight: 900 }}
          >
            {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload file'}
          </Button>
          {isHttpUrl(value) ? (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<OpenInNewIcon />}
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 900 }}
            >
              Open
            </Button>
          ) : null}
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={() => onChangeRaw?.('')}
            disabled={!value || uploading}
            sx={{ fontWeight: 900 }}
          >
            Clear
          </Button>
        </Stack>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      <Box sx={{ mt: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          required={required}
          value={value || ''}
          placeholder="https://..."
          onChange={(e) => onChangeRaw?.(e.target.value)}
          disabled={uploading}
          helperText={
            uploadError ||
            helperText ||
            'Upload a PDF, Word, Excel, or PowerPoint file, or paste a link.'
          }
          error={Boolean(uploadError)}
          inputProps={{ 'aria-label': label }}
        />
      </Box>
    </Paper>
  );
}
